import { useEffect, useMemo, useState } from 'react'
import { useCart } from '@/hooks/useCart'
import { usePageData } from '@/hooks/usePageData'
import { resolveUnitPrice } from '@/lib/ecommerce/price'
import { checkoutStorage } from '@/lib/ecommerce/checkoutStorage'
import { getShippingOption, shippingRowValue, SHIPPING_OPTION_EVENT } from '@/lib/ecommerce/shippingOptions'
import type { CheckoutPagePayload, OrderSummaryTotals } from '@/types/ecommerce'

/*
  Shared order summary panel for every checkout step. Sits in the right
  1/3 column rendered by CheckoutPageShell so the customer can see what
  they're buying as they fill in contact / shipping / billing / payment.

  Totals (Subtotal, Tax, Total) are
  computed server-side by the addon's
  `modules/ecommerce/checkout/compute_order_summary` partial and forwarded
  via each checkout page's `order_summary` payload. We avoid recomputing
  in React because the addon's tax/discount logic depends on rules
  (is_tax_included, sale price tax, fixed-amount tax, percentage discounts)
  that are easier to keep correct in one place. If the payload is missing
  for some reason we fall back to a basic line-item subtotal.

  All styling lives in src/styles/components/order-summary.css. The
  thumbnail's dynamic image URL is passed through the `--bg-image` CSS
  custom property on each item so the className-only styling still
  picks up the per-item URL.
*/

function formatMoney(amount: number): string {
  return `$${amount.toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

interface SummaryRowProps {
  label: string
  value: string
  bold?: boolean
}
function SummaryRow({ label, value, bold }: SummaryRowProps) {
  return (
    <div className="mig-summary-row" data-bold={!!bold}>
      <span>{label}</span>
      <span className="mig-summary-row-value">{value}</span>
    </div>
  )
}

export default function OrderSummary() {
  const { items, subtotal, refresh } = useCart()
  const payload = usePageData<CheckoutPagePayload>() ?? null
  const totals: OrderSummaryTotals | null = payload?.order_summary ?? null
  const [ready, setReady] = useState(false)

  useEffect(() => {
    refresh().finally(() => setReady(true))
  }, [refresh])

  // UAT-36: the chosen shipping option drives the shipping line + the
  // total here on the client. The server's order_summary stays $0 for
  // shipping (the option isn't known at GET); we add the flat fee from
  // the buyer's selection so the displayed total matches what the
  // server charges (callback derives the same fee from shipping_option).
  // Seed from the persisted mirror (set on the shipping step's submit);
  // update live as the buyer clicks via the shipping-option event.
  const [optionKey, setOptionKey] = useState<string>(
    () => checkoutStorage.readShipping()?.shipping_option ?? '',
  )
  useEffect(() => {
    const onOption = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail
      setOptionKey(typeof detail === 'string' ? detail : '')
    }
    window.addEventListener(SHIPPING_OPTION_EVENT, onOption as EventListener)
    return () => window.removeEventListener(SHIPPING_OPTION_EVENT, onOption as EventListener)
  }, [])

  const shippingOption = getShippingOption(optionKey)
  const shippingFee = shippingOption?.amount ?? 0

  // Use server-computed totals when available; fall back to client-side
  // sums if the wrapper hasn't been updated to include the partial yet.
  // The server total never includes the shipping option fee, so add it.
  const display = useMemo(() => {
    if (totals) {
      return {
        subtotal: totals.sub_total_amount,
        tax: totals.tax_amount,
        total: totals.total_amount + shippingFee,
      }
    }
    return {
      subtotal,
      tax: 0,
      total: subtotal + shippingFee,
    }
  }, [totals, subtotal, shippingFee])

  return (
    <aside className="mig-checkout-summary" aria-label="Order summary">
      <h2 className="mig-checkout-summary-title">Order summary</h2>

      {!ready && <p className="mig-checkout-summary-status">Loading cart…</p>}

      {ready && items.length === 0 && (
        <p className="mig-checkout-summary-status">
          Cart is empty — <a href="/products">continue shopping</a>.
        </p>
      )}

      {ready && items.length > 0 && (
        <div className="mig-checkout-summary-body">
          {/* Line items */}
          <ul className="mig-checkout-summary-items">
            {items.map((item) => {
              const unit = resolveUnitPrice(item.product)
              // Each artwork is one-of-a-kind (qty always 1) — no qty multiplier.
              const line = unit
              const galleryFallback = item.product?.gallery_images?.find((g) => g?.file?.url)?.file?.url
              const imageUrl =
                item.product?.main_image?.url ??
                item.product?.product_image?.url ??
                galleryFallback ??
                null
              return (
                <li key={item.uuid} className="mig-checkout-summary-item">
                  <div
                    className="mig-checkout-summary-thumb"
                    style={imageUrl ? ({ '--bg-image': `url("${imageUrl}")` } as React.CSSProperties) : undefined}
                    aria-hidden="true"
                  />
                  <div className="mig-checkout-summary-item-text">
                    <p className="mig-checkout-summary-item-name">
                      {item.product?.product_name ?? 'Artwork'}
                    </p>
                  </div>
                  <div className="mig-checkout-summary-item-price">
                    <p>{formatMoney(line)}</p>
                  </div>
                </li>
              )
            })}
          </ul>

          {/* HR + charges + HR + total — mirrors the /confirmation page's
              order-summary structure so the checkout and the post-purchase
              page render the same charges layout. */}
          <hr className="mig-checkout-summary-hr" />

          <div className="mig-checkout-summary-charges">
            <SummaryRow label="Subtotal" value={formatMoney(display.subtotal)} />
            <SummaryRow label="Tax" value={formatMoney(display.tax)} />
            {shippingOption && (
              <SummaryRow label="Shipping" value={shippingRowValue(optionKey)} />
            )}
          </div>

          <hr className="mig-checkout-summary-hr mig-checkout-summary-hr--total" />

          <SummaryRow label="Total" value={formatMoney(display.total)} bold />
        </div>
      )}
    </aside>
  )
}
